/**
 * NavigatorX System Health Dashboard
 *
 * REAL system monitoring — every metric is a live Firestore query.
 * ZERO mock data. ZERO hardcoded numbers.
 *
 * Data sources:
 * - Firebase connectivity: live read + auth checks with measured latency
 * - Well databases (OK, TX, KS): nav_data_sources collection (written by ETL cloud functions)
 * - Collection counts: getCountFromServer against real collections
 * - Error monitoring: nav_error_logs collection (written by Android AnalyticsService.logError())
 * - Infrastructure metrics: nav_infrastructure_metrics collection (written by collectInfraMetrics Cloud Function)
 *
 * Well database ETL functions run weekly from C:\apps\navigatorx-android:
 * - Oklahoma: ok-occ-etl (Sunday 4 AM CT)
 * - Kansas: ks-kgs-etl (Sunday 3 AM CT)
 * - Texas: tx-rrc-etl (scheduled via Cloud Tasks)
 *
 * @author Claude AI for TGE.LA
 * @version 3.0.0
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Activity,
  Database,
  HardDrive,
  Users,
  Zap,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Loader2,
  MessageSquare,
  BarChart3,
  Calendar,
  AlertCircle,
  Gauge,
  Bug,
  Server,
  Shield,
  Cpu,
  Timer,
  TrendingUp,
  Wifi
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Timestamp,
  collection,
  getCountFromServer,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs
} from 'firebase/firestore';
import { navDb } from '../../../utils/firebase/client';
import { getAuth } from 'firebase/auth';
import { getAllDataSources, type DataSourceStatus } from '../../../utils/services/navigatorXWellService';

// ============================================================================
// TYPES
// ============================================================================

interface SystemHealthDashboardProps {
  embedded?: boolean;
}

interface ServiceCheck {
  name: string;
  status: 'operational' | 'degraded' | 'error' | 'checking';
  latency: number | null;
  lastChecked: Date;
  message?: string;
  icon: 'database' | 'auth' | 'storage';
}

interface CollectionMetrics {
  navUserCount: number | null;
  navEventCount: number | null;
  supportTicketCount: number | null;
  hazardCount: number | null;
}

interface ErrorLogEntry {
  errorType: string;
  errorCategory: string;
  errorMessage: string;
  context: string;
  userId: string;
  timestamp: Timestamp | null;
  deviceModel?: string;
}

interface ErrorMetrics {
  totalErrors24h: number;
  errorsByCategory: Record<string, number>;
  errorsByType: Record<string, number>;
  recentErrors: ErrorLogEntry[];
}

interface InfraMetrics {
  firestore: {
    collections: Record<string, number>;
    totalDocuments: number;
  } | null;
  auth: {
    totalUsers: number;
    recentSignups24h: number;
  } | null;
  errors: {
    last24h: number;
    byCategory: Record<string, number>;
    byType: Record<string, number>;
  } | null;
  collectedAt: string | null;
}

interface PerformanceMetrics {
  /** Average session duration in seconds (from nav_analytics/session_stats_*) */
  avgSessionDurationSec: number;
  /** Total sessions measured */
  totalSessions: number;
  /** Total events in last 24h (throughput indicator) */
  eventsLast24h: number;
  /** Events per hour average */
  eventsPerHour: number;
  /** Error rate = errors / total events in last 24h */
  errorRatePct: number;
  /** Firestore read latency measured during this dashboard load */
  firestoreLatencyMs: number;
  /** Session stats date range */
  sessionStatsDate: string | null;
  /** Min session duration */
  minSessionDurationSec: number;
  /** Max session duration */
  maxSessionDurationSec: number;
  /** Sessions with measured duration */
  sessionsWithDuration: number;
}

/** Display names for state database sources */
const STATE_DB_DISPLAY: Record<string, string> = {
  oklahoma: 'Oklahoma Well Database',
  texas: 'Texas Well Database',
  kansas: 'Kansas Well Database',
};

// ============================================================================
// COMPONENT
// ============================================================================

export default function SystemHealthDashboard({ embedded = false }: SystemHealthDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [services, setServices] = useState<ServiceCheck[]>([]);
  const [dataSources, setDataSources] = useState<DataSourceStatus[]>([]);
  const [metrics, setMetrics] = useState<CollectionMetrics | null>(null);
  const [errorMetrics, setErrorMetrics] = useState<ErrorMetrics | null>(null);
  const [infraMetrics, setInfraMetrics] = useState<InfraMetrics | null>(null);
  const [perfMetrics, setPerfMetrics] = useState<PerformanceMetrics | null>(null);
  const [overallHealth, setOverallHealth] = useState<'healthy' | 'degraded' | 'critical'>('healthy');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // --------------------------------------------------------------------------
  // DATA LOADING — all real, all Firestore
  // --------------------------------------------------------------------------

  const loadAllData = useCallback(async () => {
    if (!loading) setRefreshing(true);

    try {
      const [serviceResults, sourceResults, metricResults, errorResults, infraResults, perfResults] = await Promise.allSettled([
        runServiceChecks(),
        loadDataSources(),
        loadCollectionCounts(),
        loadErrorMetrics(),
        loadInfraMetrics(),
        loadPerformanceMetrics()
      ]);

      if (serviceResults.status === 'fulfilled') setServices(serviceResults.value);
      if (sourceResults.status === 'fulfilled') setDataSources(sourceResults.value);
      if (metricResults.status === 'fulfilled') setMetrics(metricResults.value);
      if (errorResults.status === 'fulfilled') setErrorMetrics(errorResults.value);
      if (infraResults.status === 'fulfilled') setInfraMetrics(infraResults.value);
      if (perfResults.status === 'fulfilled') setPerfMetrics(perfResults.value);

      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading system health:', error);
      toast.error('Failed to load system health data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loading]);

  useEffect(() => {
    loadAllData();
    const interval = setInterval(loadAllData, 60000);
    return () => clearInterval(interval);
  }, []);

  // Recompute overall health whenever services or dataSources change
  useEffect(() => {
    const hasServiceError = services.some(s => s.status === 'error');
    const hasServiceDegraded = services.some(s => s.status === 'degraded');
    const hasSourceError = dataSources.some(s => s.status === 'error');
    const hasSourceDegraded = dataSources.some(s => s.status === 'degraded');
    // Also flag stale syncs (>14 days) as errors
    const hasStaleSync = dataSources.some(s => {
      if (!s.lastSync) return true;
      const d = s.lastSync instanceof Timestamp ? s.lastSync.toDate() : new Date(s.lastSync as any);
      return (Date.now() - d.getTime()) / 86400000 > 14;
    });
    // High error rate = degraded
    const highErrorRate = errorMetrics && errorMetrics.totalErrors24h > 50;

    if (hasServiceError || hasSourceError || hasStaleSync) {
      setOverallHealth('critical');
    } else if (hasServiceDegraded || hasSourceDegraded || highErrorRate) {
      setOverallHealth('degraded');
    } else {
      setOverallHealth('healthy');
    }
  }, [services, dataSources, errorMetrics]);

  // --------------------------------------------------------------------------
  // REAL SERVICE CHECKS — measured latency against live Firebase
  // --------------------------------------------------------------------------

  async function runServiceChecks(): Promise<ServiceCheck[]> {
    const [firestore, auth] = await Promise.allSettled([
      checkFirestore(),
      checkFirebaseAuth()
    ]);

    const results: ServiceCheck[] = [];
    if (firestore.status === 'fulfilled') results.push(firestore.value);
    else results.push({ name: 'Firebase Firestore', status: 'error', latency: null, lastChecked: new Date(), message: 'Check failed', icon: 'database' });

    if (auth.status === 'fulfilled') results.push(auth.value);
    else results.push({ name: 'Firebase Auth', status: 'error', latency: null, lastChecked: new Date(), message: 'Check failed', icon: 'auth' });

    // Storage: SDK is always present if Firestore works, no lightweight ping available
    results.push({
      name: 'Firebase Storage',
      status: results[0].status === 'operational' ? 'operational' : 'degraded',
      latency: null,
      lastChecked: new Date(),
      message: results[0].status === 'operational' ? 'SDK initialized' : 'Status inferred from Firestore',
      icon: 'storage'
    });

    return results;
  }

  /** Real Firestore connectivity check — reads a lightweight config doc and measures latency */
  async function checkFirestore(): Promise<ServiceCheck> {
    const start = performance.now();
    try {
      const configDoc = await getDoc(doc(navDb, 'nav_config', 'app_settings'));
      const latency = Math.round(performance.now() - start);
      return {
        name: 'Firebase Firestore',
        status: latency > 3000 ? 'degraded' : 'operational',
        latency,
        lastChecked: new Date(),
        message: configDoc.exists() ? 'Connected' : 'Connected (no config doc yet)',
        icon: 'database'
      };
    } catch (error: any) {
      return {
        name: 'Firebase Firestore',
        status: 'error',
        latency: Math.round(performance.now() - start),
        lastChecked: new Date(),
        message: error.code || error.message || 'Connection failed',
        icon: 'database'
      };
    }
  }

  /** Real Firebase Auth check — verifies the current user session is active */
  async function checkFirebaseAuth(): Promise<ServiceCheck> {
    const start = performance.now();
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      const latency = Math.round(performance.now() - start);
      return {
        name: 'Firebase Auth',
        status: user ? 'operational' : 'degraded',
        latency,
        lastChecked: new Date(),
        message: user ? `Signed in as ${user.email}` : 'No active session',
        icon: 'auth'
      };
    } catch (error: any) {
      return {
        name: 'Firebase Auth',
        status: 'error',
        latency: Math.round(performance.now() - start),
        lastChecked: new Date(),
        message: error.message || 'Auth check failed',
        icon: 'auth'
      };
    }
  }

  // --------------------------------------------------------------------------
  // REAL DATA SOURCE STATUS — from nav_data_sources (written by ETL functions)
  // --------------------------------------------------------------------------

  async function loadDataSources(): Promise<DataSourceStatus[]> {
    try {
      return await getAllDataSources();
    } catch (error) {
      console.error('Failed to load data sources:', error);
      return [];
    }
  }

  // --------------------------------------------------------------------------
  // REAL COLLECTION COUNTS — server-side aggregation, no full reads
  // --------------------------------------------------------------------------

  async function loadCollectionCounts(): Promise<CollectionMetrics> {
    const results: CollectionMetrics = {
      navUserCount: null,
      navEventCount: null,
      supportTicketCount: null,
      hazardCount: null,
    };

    const [users, events, tickets, hazards] = await Promise.allSettled([
      getCountFromServer(collection(navDb, 'nav_users')),
      getCountFromServer(collection(navDb, 'nav_events')),
      getCountFromServer(collection(navDb, 'nav_support_tickets')),
      getCountFromServer(collection(navDb, 'nav_hazards')),
    ]);

    if (users.status === 'fulfilled') results.navUserCount = users.value.data().count;
    if (events.status === 'fulfilled') results.navEventCount = events.value.data().count;
    if (tickets.status === 'fulfilled') results.supportTicketCount = tickets.value.data().count;
    if (hazards.status === 'fulfilled') results.hazardCount = hazards.value.data().count;

    return results;
  }

  // --------------------------------------------------------------------------
  // ERROR MONITORING — from nav_error_logs (written by Android AnalyticsService)
  // --------------------------------------------------------------------------

  async function loadErrorMetrics(): Promise<ErrorMetrics> {
    const result: ErrorMetrics = {
      totalErrors24h: 0,
      errorsByCategory: {},
      errorsByType: {},
      recentErrors: [],
    };

    try {
      const oneDayAgoDate = new Date();
      oneDayAgoDate.setDate(oneDayAgoDate.getDate() - 1);
      const oneDayAgoTs = Timestamp.fromDate(oneDayAgoDate);

      // Get count of errors in last 24h
      const countSnapshot = await getCountFromServer(
        query(
          collection(navDb, 'nav_error_logs'),
          where('timestamp', '>=', oneDayAgoTs)
        )
      );
      result.totalErrors24h = countSnapshot.data().count;

      // Get recent error documents for breakdown and log
      const recentQuery = query(
        collection(navDb, 'nav_error_logs'),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      const recentSnapshot = await getDocs(recentQuery);

      const oneDayAgoMs = oneDayAgoDate.getTime();

      for (const docSnap of recentSnapshot.docs) {
        const data = docSnap.data();
        const ts = data.timestamp instanceof Timestamp ? data.timestamp : null;
        const tsMs = ts ? ts.toMillis() : 0;

        // Build error entry for recent errors list
        result.recentErrors.push({
          errorType: data.errorType || 'Unknown',
          errorCategory: data.errorCategory || 'Unknown',
          errorMessage: data.errorMessage || '',
          context: data.context || '',
          userId: data.userId || 'anonymous',
          timestamp: ts,
          deviceModel: data.deviceModel,
        });

        // Only count for category/type breakdown if within last 24h
        if (tsMs > oneDayAgoMs) {
          if (data.errorCategory) {
            result.errorsByCategory[data.errorCategory] = (result.errorsByCategory[data.errorCategory] || 0) + 1;
          }
          if (data.errorType) {
            result.errorsByType[data.errorType] = (result.errorsByType[data.errorType] || 0) + 1;
          }
        }
      }
    } catch (error) {
      console.error('Failed to load error metrics:', error);
    }

    return result;
  }

  // --------------------------------------------------------------------------
  // INFRASTRUCTURE METRICS — from nav_infrastructure_metrics/latest
  // --------------------------------------------------------------------------

  async function loadInfraMetrics(): Promise<InfraMetrics> {
    const result: InfraMetrics = {
      firestore: null,
      auth: null,
      errors: null,
      collectedAt: null,
    };

    try {
      const latestDoc = await getDoc(doc(navDb, 'nav_infrastructure_metrics', 'latest'));
      if (latestDoc.exists()) {
        const data = latestDoc.data();
        result.firestore = data.firestore || null;
        result.auth = data.auth || null;
        result.errors = data.errors || null;
        result.collectedAt = data.collectedAt || null;
      }
    } catch (error) {
      console.error('Failed to load infrastructure metrics:', error);
    }

    return result;
  }

  // --------------------------------------------------------------------------
  // PERFORMANCE METRICS — computed from nav_events, nav_analytics, nav_error_logs
  // --------------------------------------------------------------------------

  async function loadPerformanceMetrics(): Promise<PerformanceMetrics> {
    const result: PerformanceMetrics = {
      avgSessionDurationSec: 0,
      totalSessions: 0,
      eventsLast24h: 0,
      eventsPerHour: 0,
      errorRatePct: 0,
      firestoreLatencyMs: 0,
      sessionStatsDate: null,
      minSessionDurationSec: 0,
      maxSessionDurationSec: 0,
      sessionsWithDuration: 0,
    };

    try {
      // 1. Measure Firestore read latency (real-time)
      const latencyStart = performance.now();
      await getDoc(doc(navDb, 'nav_config', 'app_settings'));
      result.firestoreLatencyMs = Math.round(performance.now() - latencyStart);

      // 2. Get event throughput (events in last 24h from nav_events)
      const oneDayAgoDate = new Date();
      oneDayAgoDate.setDate(oneDayAgoDate.getDate() - 1);
      const oneDayAgoTs = Timestamp.fromDate(oneDayAgoDate);

      const [eventCountResult, errorCountResult] = await Promise.allSettled([
        getCountFromServer(
          query(collection(navDb, 'nav_events'), where('timestamp', '>=', oneDayAgoTs))
        ),
        getCountFromServer(
          query(collection(navDb, 'nav_error_logs'), where('timestamp', '>=', oneDayAgoTs))
        ),
      ]);

      if (eventCountResult.status === 'fulfilled') {
        result.eventsLast24h = eventCountResult.value.data().count;
        result.eventsPerHour = Math.round((result.eventsLast24h / 24) * 10) / 10;
      }

      let errorCount24h = 0;
      if (errorCountResult.status === 'fulfilled') {
        errorCount24h = errorCountResult.value.data().count;
      }

      // Error rate = errors / (events + errors) * 100
      const totalActivity = result.eventsLast24h + errorCount24h;
      if (totalActivity > 0) {
        result.errorRatePct = Math.round((errorCount24h / totalActivity) * 10000) / 100;
      }

      // 3. Get session stats from nav_analytics (most recent session_stats_ document)
      try {
        const sessionQuery = query(
          collection(navDb, 'nav_analytics'),
          where('__name__', '>=', 'session_stats_'),
          where('__name__', '<=', 'session_stats_\uf8ff'),
          orderBy('__name__', 'desc'),
          limit(1)
        );
        const sessionSnapshot = await getDocs(sessionQuery);

        if (!sessionSnapshot.empty) {
          const sessionData = sessionSnapshot.docs[0].data();
          result.avgSessionDurationSec = sessionData.avgDurationSec || 0;
          result.totalSessions = sessionData.totalSessions || 0;
          result.minSessionDurationSec = sessionData.minDurationSec || 0;
          result.maxSessionDurationSec = sessionData.maxDurationSec || 0;
          result.sessionsWithDuration = sessionData.sessionsWithDuration || 0;
          result.sessionStatsDate = sessionData.date || null;
        }
      } catch (sessionError) {
        // Session stats query may fail if no documents exist yet — that's fine
        console.warn('Session stats query failed (collection may be empty):', sessionError);
      }
    } catch (error) {
      console.error('Failed to load performance metrics:', error);
    }

    return result;
  }

  // --------------------------------------------------------------------------
  // HELPERS
  // --------------------------------------------------------------------------

  const formatTimeAgo = (timestamp: Timestamp | Date | undefined | null): string => {
    if (!timestamp) return 'Never';
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp as any);
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const formatDate = (timestamp: Timestamp | Date | undefined | null): string => {
    if (!timestamp) return 'Never synced';
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp as any);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatNumber = (n: number | null | undefined): string => {
    if (n === null || n === undefined) return '\u2014';
    return n.toLocaleString();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'degraded':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'error':
      case 'outage':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'checking':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
      case 'healthy':
        return 'bg-green-100 text-green-700';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-700';
      case 'error':
      case 'outage':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getServiceIcon = (iconType: string) => {
    switch (iconType) {
      case 'database': return Database;
      case 'auth': return Users;
      case 'storage': return HardDrive;
      default: return Activity;
    }
  };

  /**
   * Determine staleness for a weekly ETL sync.
   * >8 days since last sync = degraded (missed one cycle)
   * >14 days since last sync = error (missed two cycles)
   */
  const getDbSyncHealth = (source: DataSourceStatus): 'healthy' | 'degraded' | 'error' => {
    if (source.status === 'error') return 'error';
    if (!source.lastSync) return 'error';
    const lastSyncDate = source.lastSync instanceof Timestamp
      ? source.lastSync.toDate()
      : new Date(source.lastSync as any);
    const daysSinceSync = (Date.now() - lastSyncDate.getTime()) / 86400000;
    if (daysSinceSync > 14) return 'error';
    if (daysSinceSync > 8) return 'degraded';
    return source.status || 'healthy';
  };

  /** Get error severity color based on count */
  const getErrorSeverityColor = (count: number): string => {
    if (count === 0) return 'text-green-600';
    if (count < 10) return 'text-yellow-600';
    if (count < 50) return 'text-orange-600';
    return 'text-red-600';
  };

  /** Category icon mapping */
  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'network': return <Zap className="h-3.5 w-3.5" />;
      case 'auth': return <Shield className="h-3.5 w-3.5" />;
      case 'database': return <Database className="h-3.5 w-3.5" />;
      default: return <Bug className="h-3.5 w-3.5" />;
    }
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6'}>
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">System Health</h2>
              <p className="text-gray-500">
                Live monitoring — checked {formatTimeAgo(lastRefresh)}
              </p>
            </div>
            <Badge className={
              overallHealth === 'healthy' ? 'bg-green-500 text-white' :
              overallHealth === 'degraded' ? 'bg-yellow-500 text-white' :
              'bg-red-500 text-white'
            }>
              {overallHealth === 'healthy' ? 'All Systems Operational' :
               overallHealth === 'degraded' ? 'Degraded Performance' :
               'System Issues Detected'}
            </Badge>
          </div>
          <Button variant="outline" onClick={loadAllData} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      )}

      {/* Firebase Service Checks */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Firebase Services
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {services.map(service => {
              const Icon = getServiceIcon(service.icon);
              return (
                <div key={service.name} className="p-4 border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusIcon(service.status)}
                    <Icon className="h-4 w-4 text-gray-400" />
                  </div>
                  <p className="font-medium text-sm">{service.name}</p>
                  {service.message && (
                    <p className="text-xs text-gray-500 mt-1 truncate" title={service.message}>
                      {service.message}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <Badge className={getStatusColor(service.status)}>
                      {service.status}
                    </Badge>
                    {service.latency !== null && (
                      <span className={`text-xs ${service.latency > 2000 ? 'text-orange-500 font-medium' : 'text-gray-500'}`}>
                        {service.latency}ms
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Well Database Status — real data from nav_data_sources */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Well Databases
            <span className="text-xs font-normal text-gray-500 ml-2">
              Synced weekly by ETL cloud functions
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dataSources.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Database className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="font-medium">No data source records found</p>
              <p className="text-sm mt-1">
                The <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">nav_data_sources</code> collection
                is empty. ETL cloud functions write sync status here after each weekly run.
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Expected sources: Oklahoma (ok-occ-etl), Texas (tx-rrc-etl), Kansas (ks-kgs-etl)
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {dataSources.map(source => {
                const syncHealth = getDbSyncHealth(source);
                const displayName = STATE_DB_DISPLAY[source.source] || source.source;
                return (
                  <div key={source.source} className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusIcon(syncHealth)}
                      <Database className="h-4 w-4 text-gray-400" />
                    </div>
                    <p className="font-medium text-sm">{displayName}</p>

                    {/* Record count */}
                    <div className="flex items-center gap-1.5 mt-2 text-sm">
                      <BarChart3 className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      <span className="text-gray-600">
                        {formatNumber(source.recordCount)} wells
                      </span>
                    </div>

                    {/* Last sync time */}
                    <div className="flex items-center gap-1.5 mt-1.5 text-sm">
                      <Calendar className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      <span className="text-gray-600">
                        Synced {formatTimeAgo(source.lastSync)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 ml-5">
                      {formatDate(source.lastSync)}
                    </p>

                    {/* Sync duration */}
                    {source.syncDuration != null && source.syncDuration > 0 && (
                      <div className="flex items-center gap-1.5 mt-1.5 text-sm">
                        <Clock className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                        <span className="text-gray-600">
                          Took {source.syncDuration < 60
                            ? `${source.syncDuration}s`
                            : `${Math.round(source.syncDuration / 60)}m ${source.syncDuration % 60}s`}
                        </span>
                      </div>
                    )}

                    {/* Errors during sync */}
                    {source.errorCount > 0 && (
                      <div className="flex items-center gap-1.5 mt-1.5 text-sm text-red-600">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        <span>{formatNumber(source.errorCount)} errors in last sync</span>
                      </div>
                    )}

                    {/* Last error message */}
                    {source.lastError && (
                      <p className="text-xs text-red-500 mt-1.5 bg-red-50 p-2 rounded truncate" title={source.lastError}>
                        {source.lastError}
                      </p>
                    )}

                    {/* Status badge */}
                    <div className="mt-3">
                      <Badge className={getStatusColor(syncHealth)}>
                        {syncHealth}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Monitoring — real data from nav_error_logs */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5" />
            Error Monitoring
            <span className="text-xs font-normal text-gray-500 ml-2">
              From nav_error_logs (written by Android AnalyticsService)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {errorMetrics === null ? (
            <div className="text-center py-6 text-gray-400">
              <Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin" />
              <p className="text-sm">Loading error data...</p>
            </div>
          ) : (
            <div>
              {/* Error summary stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="p-3 border rounded-lg text-center">
                  <AlertCircle className={`h-5 w-5 mx-auto mb-1 ${getErrorSeverityColor(errorMetrics.totalErrors24h)}`} />
                  <p className={`text-2xl font-bold ${getErrorSeverityColor(errorMetrics.totalErrors24h)}`}>
                    {formatNumber(errorMetrics.totalErrors24h)}
                  </p>
                  <p className="text-xs text-gray-500">Errors (24h)</p>
                </div>
                <div className="p-3 border rounded-lg text-center">
                  <BarChart3 className="h-5 w-5 mx-auto mb-1 text-blue-500" />
                  <p className="text-2xl font-bold">
                    {Object.keys(errorMetrics.errorsByCategory).length}
                  </p>
                  <p className="text-xs text-gray-500">Error Categories</p>
                </div>
                <div className="p-3 border rounded-lg text-center">
                  <Bug className="h-5 w-5 mx-auto mb-1 text-purple-500" />
                  <p className="text-2xl font-bold">
                    {Object.keys(errorMetrics.errorsByType).length}
                  </p>
                  <p className="text-xs text-gray-500">Error Types</p>
                </div>
                <div className="p-3 border rounded-lg text-center">
                  <Activity className="h-5 w-5 mx-auto mb-1 text-gray-500" />
                  <p className="text-2xl font-bold">
                    {errorMetrics.recentErrors.length}
                  </p>
                  <p className="text-xs text-gray-500">Recent Entries</p>
                </div>
              </div>

              {/* Error breakdown by category */}
              {Object.keys(errorMetrics.errorsByCategory).length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Errors by Category (24h)</h4>
                  <div className="space-y-2">
                    {Object.entries(errorMetrics.errorsByCategory)
                      .sort((a, b) => b[1] - a[1])
                      .map(([category, count]) => (
                        <div key={category} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center gap-2">
                            {getCategoryIcon(category)}
                            <span className="text-sm font-medium">{category}</span>
                          </div>
                          <Badge variant="outline" className={getErrorSeverityColor(count)}>
                            {count}
                          </Badge>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Recent error log */}
              {errorMetrics.recentErrors.length > 0 ? (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Error Log</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-48 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-medium text-gray-500">Time</th>
                            <th className="text-left p-2 font-medium text-gray-500">Type</th>
                            <th className="text-left p-2 font-medium text-gray-500">Category</th>
                            <th className="text-left p-2 font-medium text-gray-500">Message</th>
                            <th className="text-left p-2 font-medium text-gray-500">Context</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {errorMetrics.recentErrors.slice(0, 20).map((error, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="p-2 text-gray-500 whitespace-nowrap">
                                {error.timestamp ? formatTimeAgo(error.timestamp) : '\u2014'}
                              </td>
                              <td className="p-2 font-mono text-red-600">{error.errorType}</td>
                              <td className="p-2">
                                <Badge variant="outline" className="text-[10px]">{error.errorCategory}</Badge>
                              </td>
                              <td className="p-2 text-gray-600 max-w-[200px] truncate" title={error.errorMessage}>
                                {error.errorMessage}
                              </td>
                              <td className="p-2 text-gray-400">{error.context}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-400">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-400" />
                  <p className="text-sm font-medium text-green-600">No errors logged</p>
                  <p className="text-xs mt-1">
                    The <code className="bg-gray-100 px-1 py-0.5 rounded">nav_error_logs</code> collection
                    is empty. Errors will appear here once the Android app starts writing via AnalyticsService.logError().
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Infrastructure Metrics — from nav_infrastructure_metrics/latest */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Infrastructure Metrics
            <span className="text-xs font-normal text-gray-500 ml-2">
              {infraMetrics?.collectedAt
                ? `Last collected: ${new Date(infraMetrics.collectedAt).toLocaleString()}`
                : 'From collectInfraMetrics Cloud Function (every 6h)'}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {infraMetrics === null || (!infraMetrics.firestore && !infraMetrics.auth) ? (
            <div className="text-center py-8 text-gray-500">
              <Server className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="font-medium">No infrastructure metrics yet</p>
              <p className="text-sm mt-1">
                The <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">nav_infrastructure_metrics</code> collection
                is empty. Deploy and run the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">collectInfraMetrics</code> Cloud Function to populate this.
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Runs automatically every 6 hours after deployment
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Auth metrics */}
              {infraMetrics.auth && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                    <Users className="h-4 w-4" />
                    Firebase Authentication
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 border rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">{formatNumber(infraMetrics.auth.totalUsers)}</p>
                      <p className="text-xs text-gray-500">Total Auth Users</p>
                    </div>
                    <div className="p-3 border rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{formatNumber(infraMetrics.auth.recentSignups24h)}</p>
                      <p className="text-xs text-gray-500">Signups (24h)</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Firestore collection counts from infra metrics */}
              {infraMetrics.firestore && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                    <Database className="h-4 w-4" />
                    Firestore Collections
                    <span className="text-xs font-normal text-gray-400">
                      ({formatNumber(infraMetrics.firestore.totalDocuments)} total documents)
                    </span>
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {Object.entries(infraMetrics.firestore.collections)
                      .filter(([, count]) => count >= 0)
                      .sort((a, b) => b[1] - a[1])
                      .map(([name, count]) => (
                        <div key={name} className="p-2 border rounded text-center">
                          <p className="text-lg font-bold">{formatNumber(count)}</p>
                          <p className="text-[10px] text-gray-500 font-mono truncate" title={name}>{name}</p>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Error summary from infra metrics */}
              {infraMetrics.errors && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                    <AlertCircle className="h-4 w-4" />
                    Error Summary (from Cloud Function)
                  </h4>
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Errors in last 24h</span>
                      <span className={`text-lg font-bold ${getErrorSeverityColor(infraMetrics.errors.last24h)}`}>
                        {formatNumber(infraMetrics.errors.last24h)}
                      </span>
                    </div>
                    {Object.keys(infraMetrics.errors.byCategory).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {Object.entries(infraMetrics.errors.byCategory).map(([cat, count]) => (
                          <Badge key={cat} variant="outline" className="text-[10px]">
                            {cat}: {count}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Performance Metrics — real data from nav_events, nav_analytics, nav_error_logs */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cpu className="h-5 w-5" />
            Performance Metrics
            <span className="text-xs font-normal text-gray-500 ml-2">
              {perfMetrics?.sessionStatsDate
                ? `Session data from ${perfMetrics.sessionStatsDate}`
                : 'Live from nav_events + nav_analytics + nav_error_logs'}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {perfMetrics === null ? (
            <div className="text-center py-6 text-gray-400">
              <Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin" />
              <p className="text-sm">Loading performance data...</p>
            </div>
          ) : (
            <div>
              {/* Top-level performance KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {/* Firestore Latency */}
                <div className="p-3 border rounded-lg text-center">
                  <Wifi className={`h-5 w-5 mx-auto mb-1 ${
                    perfMetrics.firestoreLatencyMs < 500 ? 'text-green-500' :
                    perfMetrics.firestoreLatencyMs < 2000 ? 'text-yellow-500' : 'text-red-500'
                  }`} />
                  <p className={`text-2xl font-bold ${
                    perfMetrics.firestoreLatencyMs < 500 ? 'text-green-600' :
                    perfMetrics.firestoreLatencyMs < 2000 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {perfMetrics.firestoreLatencyMs}ms
                  </p>
                  <p className="text-xs text-gray-500">Firestore Latency</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {perfMetrics.firestoreLatencyMs < 500 ? 'Excellent' :
                     perfMetrics.firestoreLatencyMs < 1000 ? 'Good' :
                     perfMetrics.firestoreLatencyMs < 2000 ? 'Slow' : 'Critical'}
                  </p>
                </div>

                {/* Event Throughput */}
                <div className="p-3 border rounded-lg text-center">
                  <TrendingUp className="h-5 w-5 mx-auto mb-1 text-blue-500" />
                  <p className="text-2xl font-bold text-blue-600">
                    {formatNumber(perfMetrics.eventsLast24h)}
                  </p>
                  <p className="text-xs text-gray-500">Events (24h)</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    ~{perfMetrics.eventsPerHour}/hour
                  </p>
                </div>

                {/* Error Rate */}
                <div className="p-3 border rounded-lg text-center">
                  <AlertCircle className={`h-5 w-5 mx-auto mb-1 ${
                    perfMetrics.errorRatePct < 1 ? 'text-green-500' :
                    perfMetrics.errorRatePct < 5 ? 'text-yellow-500' : 'text-red-500'
                  }`} />
                  <p className={`text-2xl font-bold ${
                    perfMetrics.errorRatePct < 1 ? 'text-green-600' :
                    perfMetrics.errorRatePct < 5 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {perfMetrics.errorRatePct}%
                  </p>
                  <p className="text-xs text-gray-500">Error Rate</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {perfMetrics.errorRatePct < 1 ? 'Healthy' :
                     perfMetrics.errorRatePct < 5 ? 'Elevated' : 'Critical'}
                  </p>
                </div>

                {/* Avg Session Duration */}
                <div className="p-3 border rounded-lg text-center">
                  <Timer className="h-5 w-5 mx-auto mb-1 text-purple-500" />
                  <p className="text-2xl font-bold text-purple-600">
                    {perfMetrics.avgSessionDurationSec > 0
                      ? perfMetrics.avgSessionDurationSec >= 60
                        ? `${Math.floor(perfMetrics.avgSessionDurationSec / 60)}m ${Math.floor(perfMetrics.avgSessionDurationSec % 60)}s`
                        : `${Math.floor(perfMetrics.avgSessionDurationSec)}s`
                      : '\u2014'}
                  </p>
                  <p className="text-xs text-gray-500">Avg Session</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {perfMetrics.totalSessions > 0
                      ? `${formatNumber(perfMetrics.totalSessions)} sessions`
                      : 'No session data yet'}
                  </p>
                </div>
              </div>

              {/* Session Details */}
              {perfMetrics.totalSessions > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                    <Timer className="h-4 w-4" />
                    Session Duration Breakdown
                    {perfMetrics.sessionStatsDate && (
                      <span className="text-xs font-normal text-gray-400 ml-1">
                        ({perfMetrics.sessionStatsDate})
                      </span>
                    )}
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-2.5 bg-gray-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-gray-700">
                        {perfMetrics.minSessionDurationSec >= 60
                          ? `${Math.floor(perfMetrics.minSessionDurationSec / 60)}m ${Math.floor(perfMetrics.minSessionDurationSec % 60)}s`
                          : `${Math.floor(perfMetrics.minSessionDurationSec)}s`}
                      </p>
                      <p className="text-xs text-gray-500">Min Duration</p>
                    </div>
                    <div className="p-2.5 bg-purple-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-purple-700">
                        {perfMetrics.avgSessionDurationSec >= 60
                          ? `${Math.floor(perfMetrics.avgSessionDurationSec / 60)}m ${Math.floor(perfMetrics.avgSessionDurationSec % 60)}s`
                          : `${Math.floor(perfMetrics.avgSessionDurationSec)}s`}
                      </p>
                      <p className="text-xs text-gray-500">Avg Duration</p>
                    </div>
                    <div className="p-2.5 bg-gray-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-gray-700">
                        {perfMetrics.maxSessionDurationSec >= 60
                          ? `${Math.floor(perfMetrics.maxSessionDurationSec / 60)}m ${Math.floor(perfMetrics.maxSessionDurationSec % 60)}s`
                          : `${Math.floor(perfMetrics.maxSessionDurationSec)}s`}
                      </p>
                      <p className="text-xs text-gray-500">Max Duration</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5 text-center">
                    {formatNumber(perfMetrics.sessionsWithDuration)} of {formatNumber(perfMetrics.totalSessions)} sessions
                    had measurable foreground/background pairs
                  </p>
                </div>
              )}

              {/* Health Indicators Bar */}
              <div className="border rounded-lg p-3">
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                  <Gauge className="h-4 w-4" />
                  Health Indicators
                </h4>
                <div className="space-y-2">
                  {/* Latency bar */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-28 shrink-0">Firestore Latency</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          perfMetrics.firestoreLatencyMs < 500 ? 'bg-green-500' :
                          perfMetrics.firestoreLatencyMs < 1000 ? 'bg-green-400' :
                          perfMetrics.firestoreLatencyMs < 2000 ? 'bg-yellow-500' :
                          perfMetrics.firestoreLatencyMs < 3000 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(100, (perfMetrics.firestoreLatencyMs / 3000) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-14 text-right">{perfMetrics.firestoreLatencyMs}ms</span>
                  </div>

                  {/* Error rate bar */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-28 shrink-0">Error Rate</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          perfMetrics.errorRatePct < 1 ? 'bg-green-500' :
                          perfMetrics.errorRatePct < 5 ? 'bg-yellow-500' :
                          perfMetrics.errorRatePct < 10 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(100, perfMetrics.errorRatePct * 10)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-14 text-right">{perfMetrics.errorRatePct}%</span>
                  </div>

                  {/* Throughput bar (normalized to 1000 events/day = full bar) */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-28 shrink-0">Event Throughput</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-blue-500 transition-all"
                        style={{ width: `${Math.min(100, (perfMetrics.eventsLast24h / 1000) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-14 text-right">{formatNumber(perfMetrics.eventsLast24h)}</span>
                  </div>
                </div>
              </div>

              {/* Empty state message when no events yet */}
              {perfMetrics.eventsLast24h === 0 && perfMetrics.totalSessions === 0 && (
                <div className="mt-4 text-center py-4 bg-blue-50 rounded-lg">
                  <Cpu className="h-6 w-6 mx-auto mb-1.5 text-blue-400" />
                  <p className="text-sm font-medium text-blue-700">Waiting for app event data</p>
                  <p className="text-xs text-blue-500 mt-1">
                    Metrics will populate once the Android app starts writing to
                    <code className="bg-blue-100 px-1 py-0.5 rounded mx-1">nav_events</code>
                    and the aggregateAnalytics Cloud Function runs.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Real Firestore Collection Counts */}
      {metrics && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Collection Counts
              <span className="text-xs font-normal text-gray-500 ml-2">
                Live from Firestore (getCountFromServer)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 border rounded-lg text-center">
                <Users className="h-5 w-5 mx-auto text-blue-500 mb-1" />
                <p className="text-2xl font-bold">{formatNumber(metrics.navUserCount)}</p>
                <p className="text-xs text-gray-500">nav_users</p>
              </div>
              <div className="p-4 border rounded-lg text-center">
                <Activity className="h-5 w-5 mx-auto text-green-500 mb-1" />
                <p className="text-2xl font-bold">{formatNumber(metrics.navEventCount)}</p>
                <p className="text-xs text-gray-500">nav_events</p>
              </div>
              <div className="p-4 border rounded-lg text-center">
                <MessageSquare className="h-5 w-5 mx-auto text-orange-500 mb-1" />
                <p className="text-2xl font-bold">{formatNumber(metrics.supportTicketCount)}</p>
                <p className="text-xs text-gray-500">nav_support_tickets</p>
              </div>
              <div className="p-4 border rounded-lg text-center">
                <AlertTriangle className="h-5 w-5 mx-auto text-red-500 mb-1" />
                <p className="text-2xl font-bold">{formatNumber(metrics.hazardCount)}</p>
                <p className="text-xs text-gray-500">nav_hazards</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
